import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { api, apiError } from "@/lib/api";
import Branding from "@/pages/lab/Branding";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { ShieldCheck, CreditCard, Settings as SettingsIcon, Plus, Loader2, Trash2, Copy, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import LabWalletCard from "@/components/lab/LabWalletCard";

export function LabTeam() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [form, setForm] = useState({ email: "", full_name: "", role_label: "Staff", branch: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/labs/team");
      setMembers(r.data.members || []);
    } catch (err) { toast.error(apiError(err, "Could not load team")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const invite = async (e) => {
    e?.preventDefault();
    if (!form.email || !form.full_name) return toast.error("Name and email are required");
    setSaving(true);
    try {
      const r = await api.post("/labs/team/invite", form);
      setInviteResult(r.data);
      toast.success(`${form.full_name} invited`);
      setForm({ email: "", full_name: "", role_label: "Staff", branch: "", phone: "" });
      load();
    } catch (err) { toast.error(apiError(err, "Invite failed")); }
    finally { setSaving(false); }
  };
  const removeMember = async (uid, name) => {
    if (!window.confirm(`Remove ${name} from your team? They'll lose access immediately.`)) return;
    try {
      await api.delete(`/labs/team/${uid}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(apiError(err, "Remove failed")); }
  };
  const copyCreds = () => {
    if (!inviteResult) return;
    const text = `Email: ${inviteResult.email}\nOne-time password: ${inviteResult.temp_password}\nLogin at ${window.location.origin}/login`;
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <DashboardLayout role="lab_admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-efgh-blue" /> Team</h1>
          <p className="text-ink/60">Manage staff access to your lab admin panel.</p>
        </div>
        <Button data-testid="invite-user-btn" onClick={() => { setInviteResult(null); setInviteOpen(true); }} className="bg-ink hover:bg-ink/90 text-white"><Plus className="w-4 h-4 mr-2" />Invite User</Button>
      </div>
      <Card className="mt-6 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-[1.4fr_1.6fr_1fr_1fr_60px] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40">
          <div>Name</div><div>Email</div><div>Role</div><div>Branch</div><div></div>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
        ) : members.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink/50">No team members yet. Invite your first colleague to collaborate.</div>
        ) : members.map((m) => {
          const initials = (m.full_name || m.email || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
          const self = m.id === user?.id;
          return (
            <div key={m.id} className="grid grid-cols-[1.4fr_1.6fr_1fr_1fr_60px] gap-3 px-5 py-3 items-center border-b border-border last:border-b-0" data-testid={`team-row-${m.id}`}>
              <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-ink text-white grid place-items-center text-xs font-heading font-bold">{initials}</div><div><div className="font-medium text-ink">{m.full_name || "—"}</div>{self && <div className="text-[10px] text-ink/50">You</div>}</div></div>
              <div className="text-sm text-ink/70 truncate">{m.email}</div>
              <div className="text-sm text-ink/70">{m.role_label || (m.role === "lab_admin" ? "Admin" : m.role)}</div>
              <div className="text-sm text-ink/70">{m.branch || "All branches"}</div>
              <div className="text-right">
                {!self && <button onClick={() => removeMember(m.id, m.full_name || m.email)} data-testid={`remove-${m.id}`} className="text-ink/40 hover:text-danger"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          );
        })}
      </Card>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md" data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Invite team member</DialogTitle></DialogHeader>
          {!inviteResult ? (
            <form onSubmit={invite} className="space-y-3">
              <div><Label>Full name</Label><Input data-testid="invite-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required /></div>
              <div><Label>Email</Label><Input data-testid="invite-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Role label</Label><Input data-testid="invite-role" value={form.role_label} onChange={(e) => setForm({ ...form, role_label: e.target.value })} placeholder="Admin / Pathologist / Reception" /></div>
                <div><Label>Branch (optional)</Label><Input data-testid="invite-branch" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} placeholder="Main / Kothrud" /></div>
              </div>
              <div><Label>Phone (optional)</Label><Input data-testid="invite-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" /></div>
              <div className="pt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="invite-submit" className="bg-ink text-white hover:bg-ink/90">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Send invite</Button>
              </div>
            </form>
          ) : (
            <div className="space-y-3" data-testid="invite-result">
              <div className="p-3 rounded-lg bg-success/10 text-success text-sm">✓ Invited <b>{inviteResult.full_name}</b>. Share the one-time password below — they can change it after first login.</div>
              <div className="p-3 rounded-lg bg-muted text-xs font-mono">
                <div><span className="text-ink/50">Email:</span> {inviteResult.email}</div>
                <div><span className="text-ink/50">One-time password:</span> <b>{inviteResult.temp_password}</b></div>
                <div className="text-ink/50 mt-1">Login URL: {window.location.origin}/login</div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={copyCreds} data-testid="copy-creds">{copied ? <><Check className="w-4 h-4 mr-2" />Copied</> : <><Copy className="w-4 h-4 mr-2" />Copy credentials</>}</Button>
                <Button onClick={() => { setInviteOpen(false); setInviteResult(null); }} className="bg-ink text-white hover:bg-ink/90">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export function LabBilling() {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [commissions, setCommissions] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [busyPlan, setBusyPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get("/payments/subscription/current"),
        api.get("/public/platform-settings"),
      ]);
      setCurrent(c.data.current || null);
      setHistory(c.data.history || []);
      setPricing(p.data?.pricing || null);
      setCommissions(c.data.commissions || null);
      setBilling(c.data.current?.billing || "monthly");
    } catch (err) { toast.error(apiError(err, "Could not load billing")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const symbol = pricing?.currency?.symbol || "₹";
  const plans = pricing?.subscription_plans || [];
  const activePlanId = current?.plan_id;

  const startCheckout = async (plan) => {
    setBusyPlan(plan.id);
    try {
      const { data: order } = await api.post("/payments/subscription/order", { plan_id: plan.id, billing });
      const result = await openRazorpayCheckout({
        key_id: order.key_id,
        order_id: order.order_id,
        amount_paise: order.amount_paise,
        description: `EFGH · ${plan.name} plan (${billing})`,
        prefill: { name: user?.full_name || "", email: user?.email || "", contact: user?.phone || "9876543210" },
        theme_color: "#F97316",
      });
      if (!result) { setBusyPlan(null); return; }
      const verify = await api.post("/payments/verify", {
        razorpay_order_id: result.razorpay_order_id,
        razorpay_payment_id: result.razorpay_payment_id,
        razorpay_signature: result.razorpay_signature,
      });
      toast.success(`Plan activated — expires ${(verify.data.expires_at || "").slice(0, 10)}`);
      await load();
    } catch (err) { toast.error(apiError(err, "Payment failed")); }
    finally { setBusyPlan(null); }
  };

  return (
    <DashboardLayout role="lab_admin">
      <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><CreditCard className="w-6 h-6 text-efgh-yellow" /> Subscription & Billing</h1>
      <p className="text-ink/60">Manage your EFGH plan — upgrade, renew or downgrade anytime.</p>

      {loading ? (
        <div className="mt-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
      ) : (
        <>
          {/* Current plan card */}
          <Card className="mt-6 p-6 rounded-2xl" data-testid="current-plan-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-ink/50">Current plan</div>
                <div className="mt-1 font-heading font-bold text-2xl text-ink" data-testid="current-plan-name">
                  {current?.plan?.name || "No active plan"}
                  {current?.plan_id && (
                    <span className="ml-2 text-base text-ink/60">
                      · {symbol}{current.billing === "annual" ? (current.plan?.annual_price || 0).toLocaleString() : (current.plan?.monthly_price || 0).toLocaleString()}/{current.billing === "annual" ? "yr" : "mo"}
                    </span>
                  )}
                </div>
                <div className="text-sm text-ink/60 mt-1">
                  {current?.plan?.reports_included ? `${current.plan.reports_included.toLocaleString()} reports/${current.billing === "annual" ? "year" : "month"} included` : "Pay-as-you-go"}
                  {current?.expires_at && <> · Renews {String(current.expires_at).slice(0, 10)}</>}
                </div>
              </div>
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted">
                {["monthly", "annual"].map((b) => (
                  <button key={b} type="button" onClick={() => setBilling(b)} data-testid={`billing-toggle-${b}`}
                    className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-full font-semibold transition-all ${billing === b ? "bg-white text-ink shadow-sm" : "text-ink/50 hover:text-ink"}`}>
                    {b}{b === "annual" && <span className="ml-1 text-efgh-orange">· 2 mo. free</span>}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* Commission rates card — visible to every lab so they know what EFGH takes */}
          {commissions && (
            <Card className="mt-4 p-5 rounded-2xl" data-testid="lab-commissions-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-ink/50">EFGH commission on your bookings</div>
                  <div className="mt-2 flex gap-6 flex-wrap">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-ink/40">Marketplace</div>
                      <div className="font-heading font-bold text-2xl text-ink" data-testid="lab-comm-marketplace-value">{commissions.marketplace_percent}%</div>
                      <div className="text-[11px] text-ink/50">{commissions.source?.marketplace === "lab_override" ? "Custom rate set by EFGH admin" : "Platform default rate"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-ink/40">Follow-up (Book-Now)</div>
                      <div className="font-heading font-bold text-2xl text-ink" data-testid="lab-comm-followup-value">{commissions.followup_percent}%</div>
                      <div className="text-[11px] text-ink/50">{commissions.source?.followup === "lab_override" ? "Custom rate set by EFGH admin" : "Platform default rate"}</div>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-ink/50 max-w-[260px] text-right">Commission is deducted from your EFGH lab wallet whenever a booking settles. Recharge below to keep bookings running.</div>
              </div>
            </Card>
          )}

          {/* Lab wallet — pre-paid balance labs top up so EFGH can debit commissions */}
          <LabWalletCard />

          {/* Plans grid */}
          <div className="mt-6 grid md:grid-cols-3 gap-4" data-testid="plans-grid">
            {plans.map((p) => {
              const price = billing === "annual" ? p.annual_price : p.monthly_price;
              const isActive = activePlanId === p.id && current?.billing === billing;
              const isBusy = busyPlan === p.id;
              return (
                <Card key={p.id} data-testid={`plan-${p.id}`} className={`p-6 rounded-2xl relative ${isActive ? "ring-2 ring-efgh-green" : ""}`}>
                  {isActive && <span className="absolute -top-3 right-6 bg-efgh-green text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold">Current</span>}
                  <div className="font-heading font-bold text-lg text-ink">{p.name}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-heading font-bold text-3xl text-ink">{symbol}{Number(price || 0).toLocaleString()}</span>
                    <span className="text-sm text-ink/50">/{billing === "annual" ? "yr" : "mo"}</span>
                  </div>
                  <div className="mt-1 text-xs text-ink/60">
                    {(p.reports_included || 0).toLocaleString()} reports · {symbol}{p.per_report_overage}/overage
                  </div>
                  {(p.bonus_wallet_credit || 0) > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-efgh-orange bg-efgh-orange/10 px-2 py-1 rounded-full font-semibold" data-testid={`plan-bonus-${p.id}`}>
                      <Sparkles className="w-3 h-3" /> +{symbol}{(billing === "annual" ? p.bonus_wallet_credit * 12 : p.bonus_wallet_credit).toLocaleString()} wallet bonus
                    </div>
                  )}
                  <ul className="mt-4 space-y-1.5 text-xs text-ink/70">
                    {(p.features || []).slice(0, 5).map((f, i) => <li key={i}>✓ {f}</li>)}
                  </ul>
                  <Button
                    data-testid={`upgrade-${p.id}`}
                    disabled={isActive || isBusy || price === 0}
                    onClick={() => startCheckout(p)}
                    className={`w-full mt-5 h-10 ${isActive ? "bg-muted text-ink/50" : "bg-ink hover:bg-ink/90 text-white"}`}
                  >
                    {isBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {isActive ? "Current plan" : price === 0 ? "Free tier" : activePlanId ? "Switch" : "Upgrade"}
                  </Button>
                </Card>
              );
            })}
            {plans.length === 0 && <div className="col-span-3 p-8 text-center text-ink/50 border border-dashed border-border rounded-2xl">No plans configured. Contact your platform admin.</div>}
          </div>

          {/* Payment history */}
          <Card className="mt-6 p-6 rounded-2xl" data-testid="billing-history">
            <div className="font-heading font-semibold text-ink">Payment history</div>
            {history.length === 0 ? (
              <div className="mt-4 p-6 text-center text-ink/50 text-sm">No payments yet.</div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-5 gap-3 text-[11px] uppercase tracking-wider text-ink/50 pb-2 border-b border-border">
                  <div>Date</div><div>Plan</div><div>Billing</div><div className="text-right">Amount</div><div>Status</div>
                </div>
                {history.map((p) => (
                  <div key={p.id} className="grid grid-cols-5 gap-3 py-3 border-b border-border text-sm items-center">
                    <div className="text-ink/70">{(p.created_at || "").slice(0, 10)}</div>
                    <div className="text-ink">{p.meta?.plan_name || p.meta?.plan_id || "—"}</div>
                    <div className="text-ink/70 uppercase text-xs">{p.meta?.billing || "—"}</div>
                    <div className="text-right font-heading font-semibold text-ink">{symbol}{(p.amount_paise / 100).toLocaleString()}</div>
                    <div><span className={`pill ${p.status === "paid" ? "pill-normal" : "pill-warning"}`}>{p.status}</span></div>
                  </div>
                ))}
              </>
            )}
          </Card>
        </>
      )}
    </DashboardLayout>
  );
}

export function LabSettings() {
  return <Branding mode="settings" />;
}
